import { yupResolver } from '@hookform/resolvers/yup';
import InfoCircleSvg from 'components/_common/Icons/SVGIcons/InfoCircleSvg';
import MobileNumberInput from 'components/_common/MobileNumberInput';
import MultilineFieldError from 'components/UI/MultilineFieldError';
import TooltipOverlay from 'components/UI/TooltipOverlay';
import { allowNumberOnly } from 'helpers/CommonHelper';
import { DEFAULT_MOBILE_CODE, MOBILE_CODE_OPTION_LIST } from 'helpers/DataHelper';
import {
  dateOfBirthDayRules,
  dateOfBirthMonthRules,
  dateOfBirthRules,
  emailREGEX,
  mobileNumberREGEX,
  passwordRules,
} from 'helpers/ValidationHelper';
import { t } from 'i18next';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { executeCustomerSignUp } from 'store/auth/actions/AuthAction';
import * as Yup from 'yup';

const EyeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ color: '#9b9daf', cursor: 'pointer' }}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface BuyerSignUpFormSectionProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const defaultValue = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
  mobile_code: DEFAULT_MOBILE_CODE,
  mobile: '',
  dob_month: '',
  dob_day: '',
  dob_year: '',
  // opted out by default - the customer ticks it to opt in
  marketing_opt_in: false,
};
const initialShowPasswordState = {
  new: false,
  confirm: false,
};
const BuyerSignUpFormSection: React.FC<BuyerSignUpFormSectionProps> = ({
  onClose,
  onSwitchToLogin,
}) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(initialShowPasswordState);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(t('required_error_msg', { field_name: t('first_name') })),
    last_name: Yup.string().required(t('required_error_msg', { field_name: t('last_name') })),
    email: Yup.string()
      .required(t('required_error_msg', { field_name: t('email_address') }))
      .matches(emailREGEX, t('invalid_error_msg', { field_name: t('email_address') })),
    mobile_code: Yup.string().nullable(),
    mobile: Yup.string()
      .required(t('required_error_msg', { field_name: t('phone_number') }))
      .matches(mobileNumberREGEX, t('invalid_error_msg', { field_name: t('phone_number') })),
    password: passwordRules(),
    confirm_password: Yup.string()
      .oneOf([Yup.ref('password')], t('password_not_match'))
      .required(t('required_error_msg', { field_name: t('confirm_password') })),
    dob_month: dateOfBirthMonthRules('date_of_birth'),
    dob_day: dateOfBirthDayRules('date_of_birth'),
    dob_year: dateOfBirthRules('date_of_birth'),
    marketing_opt_in: Yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValue,
  });

  const onSubmit = async (data: any) => {
    const dobFormatted = `${data.dob_year}-${data.dob_month.padStart(2, '0')}-${data.dob_day.padStart(2, '0')}`;
    const payload = {
      ...data,
      dob: dobFormatted,
    };
    await executeCustomerSignUp(payload, setLoading, () => {
      reset(defaultValue);
      setShowPassword(initialShowPasswordState);
      onClose();
    });
  };

  return (
    <form className="login-modal-content__form" onSubmit={handleSubmit(onSubmit)}>
      <div className="form-heading-section">
        <h3>{t('welcome_back')}</h3>
        <p>{t('login_to_buy_and_manage')}</p>
      </div>

      {/* Email Address */}
      <div className="input-group-custom">
        <label htmlFor="modal-signup-email">
          {t('email_address')} <span className="text-danger">*</span>
        </label>
        <input
          id="modal-signup-email"
          type="email"
          {...register('email')}
          autoComplete="off"
          placeholder={t('email_address')}
          className={errors.email ? 'input-error' : ''}
        />
        {errors.email && <span className="error-message">{errors.email.message}</span>}
      </div>

      {/* First Name & Last Name */}
      <div className="form-row-2col">
        <div className="input-group-custom">
          <label htmlFor="modal-signup-firstname">
            {t('first_name')} <span className="text-danger">*</span>
          </label>
          <input
            id="modal-signup-firstname"
            type="text"
            {...register('first_name')}
            placeholder={t('first_name')}
            className={errors.first_name ? 'input-error' : ''}
          />
          {errors.first_name && <span className="error-message">{errors.first_name.message}</span>}
        </div>

        <div className="input-group-custom">
          <label htmlFor="modal-signup-lastname">
            {t('last_name')} <span className="text-danger">*</span>
          </label>
          <input
            id="modal-signup-lastname"
            type="text"
            {...register('last_name')}
            placeholder={t('last_name')}
            className={errors.last_name ? 'input-error' : ''}
          />
          {errors.last_name && <span className="error-message">{errors.last_name.message}</span>}
        </div>
      </div>

      {/* Date of Birth */}
      <div className="input-group-custom">
        <label>
          {t('date_of_birth')} <span className="text-danger">*</span>{' '}
          <TooltipOverlay title={t('must_be_at_least_18_years_old')} placement="right">
            <InfoCircleSvg />
          </TooltipOverlay>
        </label>
        <div className="dob-inputs-group">
          <input
            type="text"
            placeholder="MM"
            maxLength={2}
            {...register('dob_month')}
            onKeyDown={(e) => allowNumberOnly(e)}
            className={errors.dob_month ? 'input-error' : ''}
          />
          <span className="dob-slash">/</span>
          <input
            type="text"
            placeholder="DD"
            maxLength={2}
            {...register('dob_day')}
            onKeyDown={(e) => allowNumberOnly(e)}
            className={errors.dob_day ? 'input-error' : ''}
          />
          <span className="dob-slash">/</span>
          <input
            type="text"
            placeholder="YYYY"
            maxLength={4}
            {...register('dob_year')}
            onKeyDown={(e) => allowNumberOnly(e)}
            className={errors.dob_year ? 'input-error' : ''}
          />
        </div>
        {(errors.dob_month || errors.dob_day || errors.dob_year) && (
          <span className="error-message">
            {errors.dob_year?.message ||
              errors.dob_month?.message ||
              errors.dob_day?.message ||
              t('required_error_msg', { field_name: t('date_of_birth') })}
          </span>
        )}
      </div>

      {/* Phone Number */}
      <div className="input-group-custom">
        <label htmlFor="modal-signup-phone">
          {t('phone_number')} <span className="text-danger">*</span>
        </label>
        <div className="phone-select-input">
          <select {...register('mobile_code')} className="phone-code-select">
            {MOBILE_CODE_OPTION_LIST.map((opt) => (
              <option key={opt.code} value={`${opt.code}`}>
                {opt.label}
              </option>
            ))}
          </select>
          <MobileNumberInput
            plain
            control={control}
            name="mobile"
            id="modal-signup-phone"
            placeholder={t('phone_number')}
            className={errors.mobile ? 'input-error' : ''}
          />
        </div>
        {errors.mobile && <span className="error-message">{errors.mobile.message}</span>}
      </div>

      {/* Password */}
      <div className="input-group-custom">
        <label htmlFor="modal-signup-password">
          {t('password')} <span className="text-danger">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            id="modal-signup-password"
            type={showPassword.new ? 'text' : 'password'}
            {...register('password')}
            autoComplete="new-password"
            placeholder={t('password')}
            className={errors.password ? 'input-error' : ''}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowPassword((prev) => ({ ...prev, new: !prev.new }))}
          >
            {showPassword.new ? <EyeSlashIcon /> : <EyeIcon />}
          </span>
        </div>
        {errors.password && (
          <span className="error-message">
            <MultilineFieldError message={errors.password.message as string} />
          </span>
        )}
      </div>

      {/* Confirm Password */}
      <div className="input-group-custom">
        <label htmlFor="modal-signup-confirm-password">
          {t('confirm_password')} <span className="text-danger">*</span>
        </label>
        <div className="password-input-wrapper">
          <input
            id="modal-signup-confirm-password"
            type={showPassword.confirm ? 'text' : 'password'}
            {...register('confirm_password')}
            autoComplete="new-password"
            placeholder={t('confirm_password')}
            className={errors.confirm_password ? 'input-error' : ''}
          />
          <span
            className="password-toggle-icon"
            onClick={() => setShowPassword((prev) => ({ ...prev, confirm: !prev.confirm }))}
          >
            {showPassword.confirm ? <EyeSlashIcon /> : <EyeIcon />}
          </span>
        </div>
        {errors.confirm_password && (
          <span className="error-message">{errors.confirm_password.message}</span>
        )}
      </div>

      {/* Marketing opt-in */}
      <div className="forgot-remember-row marketing-opt-in-row">
        <label className="remember-checkbox-label" htmlFor="modal-signup-marketing-opt-in">
          <input
            id="modal-signup-marketing-opt-in"
            type="checkbox"
            className="custom-checkbox"
            {...register('marketing_opt_in')}
          />
          <span>{t('marketing_opt_in_label')}</span>
        </label>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="btn btn-orange login-modal-content__submit-btn"
        disabled={loading}
      >
        {loading ? t('loading') : t('sign_up')}
      </button>

      {/* Already have an account prompt */}
      <div className="form-signup-prompt">
        <p>
          Already have an account?{' '}
          <span onClick={onSwitchToLogin} className="orange-link" style={{ cursor: 'pointer' }}>
            {t('login')}
          </span>
        </p>
      </div>
    </form>
  );
};

export default BuyerSignUpFormSection;
